/**
 * InvalidRowsAiPrompt — generates a copyable AI prompt from invalid import rows.
 *
 * Shows a collapsible section at the bottom of the Invalid category accordion.
 * The prompt includes row numbers, column names, values, and error messages so
 * users can paste it into ChatGPT, Claude, etc. to get help fixing their data.
 */

import { Check, Copy01, Stars01 } from '@untitledui/icons';
import { memo, useCallback, useMemo } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CsvImport } from '@/lib/csv-import/api';
import { cx } from '@/utils/cx';

import type { RowData } from './types';
import { getPropertyLabel } from './types';

// ============================================================================
// Types
// ============================================================================

interface InvalidRowsAiPromptProps {
  rows: RowData[];
  importData: CsvImport;
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build a reverse lookup from CRM property name → CSV column header for a
 * given file. column_mappings maps csvColumn → propertyName, so we invert it.
 */
function buildPropertyToColumnMap(
  importData: CsvImport,
  fileId: string
): Record<string, string> {
  const mapping = importData.column_mappings[fileId];
  if (!mapping) {
    return {};
  }
  const reverse: Record<string, string> = {};
  for (const [csvColumn, propertyName] of Object.entries(mapping)) {
    reverse[propertyName] = csvColumn;
  }
  return reverse;
}

/** Max rows to include in the prompt to keep it a reasonable size. */
const MAX_PROMPT_ROWS = 50;

/**
 * Build the AI prompt text from invalid rows. Includes row numbers, the CSV
 * column name (not internal property names), the value that failed, and the
 * validation error.
 */
function buildPrompt(
  rows: RowData[],
  importData: CsvImport,
  schemaMap: PropertySchemaMap,
  newPropertyLabels: Record<string, string>
): string {
  const lines: string[] = [];
  lines.push(
    'I have a CSV import with validation errors. Help me fix the data in my spreadsheet so every row passes validation. Below are the rows that failed, with their row numbers, the column that caused the error, the current value, and the error message.'
  );
  lines.push('');

  // Group rows by file so each file's column mapping is used correctly
  const rowsByFile = new Map<string, RowData[]>();
  for (const row of rows) {
    const existing = rowsByFile.get(row.file_id);
    if (existing) {
      existing.push(row);
    } else {
      rowsByFile.set(row.file_id, [row]);
    }
  }

  const truncated = rows.length > MAX_PROMPT_ROWS;
  let rowCount = 0;

  for (const [fileId, fileRows] of rowsByFile) {
    const propertyToColumn = buildPropertyToColumnMap(importData, fileId);
    const file = importData.uploaded_files.find(
      f => String(f.file_id) === fileId
    );
    const filename = file?.filename ?? `File ${fileId}`;

    if (rowsByFile.size > 1) {
      lines.push(`## ${filename}`);
      lines.push('');
    }

    for (const row of fileRows) {
      if (rowCount >= MAX_PROMPT_ROWS) {
        break;
      }
      rowCount++;

      // Row number is 1-indexed for users (row_index is 0-indexed, +1 for
      // the header row that the user sees in their spreadsheet)
      const rowNum = row.row_index + 2;
      lines.push(`**Row ${rowNum}:**`);

      if (row.rawErrors && row.rawErrors.length > 0) {
        // Use the raw error array for precise property → column mapping
        for (const err of row.rawErrors) {
          const csvColumn =
            propertyToColumn[err.property_name] ??
            getPropertyLabel(err.property_name, schemaMap, newPropertyLabels);
          const value = row.data[err.property_name];
          if (value !== undefined && value !== null && value !== '') {
            lines.push(
              `- Column "${csvColumn}" (value: "${String(value)}"): ${err.error}`
            );
          } else {
            lines.push(`- Column "${csvColumn}" (empty): ${err.error}`);
          }
        }
      } else if (row.error) {
        // Fallback: use the pre-formatted error string
        lines.push(`- ${row.error}`);
      }
      lines.push('');
    }
  }

  if (truncated) {
    lines.push(
      `(Showing ${MAX_PROMPT_ROWS} of ${rows.length} invalid rows. Fix these first, then re-import to see any remaining errors.)`
    );
    lines.push('');
  }

  lines.push(
    'For each error, tell me exactly what to change in my spreadsheet to fix it. Group fixes by column when possible.'
  );

  return lines.join('\n');
}

// ============================================================================
// Component
// ============================================================================

export const InvalidRowsAiPrompt = memo(function InvalidRowsAiPrompt({
  rows,
  importData,
  schemaMap,
  newPropertyLabels,
}: InvalidRowsAiPromptProps) {
  const { copied, copy } = useClipboard();

  const prompt = useMemo(
    () => buildPrompt(rows, importData, schemaMap, newPropertyLabels),
    [rows, importData, schemaMap, newPropertyLabels]
  );

  const handleCopy = useCallback(() => {
    void copy(prompt);
  }, [copy, prompt]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <AccordionItem className='border-t border-[var(--color-border-secondary)]'>
      <AccordionTrigger className='w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-[var(--color-bg-secondary)] transition-colors'>
        <Stars01
          className='size-4 text-fg-brand-primary shrink-0'
          aria-hidden='true'
        />
        <span className='flex-1 text-sm font-medium text-fg-primary'>
          Fix with AI
        </span>
        <span className='text-xs text-fg-tertiary'>
          {rows.length} {rows.length === 1 ? 'error' : 'errors'}
        </span>
        <AccordionChevron className='size-4 text-fg-quaternary shrink-0' />
      </AccordionTrigger>
      <AccordionPanel className='border-t border-[var(--color-border-secondary)] px-4 pb-4'>
        <p className='mt-3 text-sm text-fg-secondary'>
          Copy this prompt into ChatGPT, Claude, or your preferred AI assistant.
          It includes the row numbers and error details so the AI can tell you
          exactly what to fix in your spreadsheet.
        </p>
        <div className='mt-3 relative'>
          <pre
            className={cx(
              'whitespace-pre-wrap text-xs text-fg-secondary',
              'rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-4 pr-12',
              'max-h-60 overflow-y-auto'
            )}
          >
            {prompt}
          </pre>
          <div className='absolute right-2 top-2'>
            <Button
              color='secondary'
              size='xs'
              onClick={handleCopy}
              iconLeading={copied ? Check : Copy01}
              aria-label={copied ? 'Copied' : 'Copy prompt'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
});
